import { useEffect } from "react";
import { useSelector } from "react-redux";
import { selectSettings } from "../redux/slices/homepageSlice";

const HeadTags = () => {
  const settings = useSelector(selectSettings);
  const headerTags = settings?.headTags?.headerTags || "";
  const footerTags = settings?.headTags?.footerTags || "";

  // Inject header tags into <head>
  useEffect(() => {
    if (headerTags) {
      // Create a temporary container to parse the HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = headerTags;

      // Get all elements from the parsed HTML
      const elements = Array.from(tempDiv.children);

      // Inject each element into the head
      elements.forEach((element) => {
        // Clone the element to avoid issues
        const clonedElement = element.cloneNode(true);

        // Check if element already exists (by tag name and key attributes)
        const tagName = clonedElement.tagName.toLowerCase();
        let existingElement = null;

        if (tagName === "meta") {
          const name = clonedElement.getAttribute("name");
          const property = clonedElement.getAttribute("property");
          const charset = clonedElement.getAttribute("charset");
          const httpEquiv = clonedElement.getAttribute("http-equiv");

          // Skip keywords, description, and title meta tags - let service/offer pages handle them
          if (name === "keywords" || name === "description") {
            return; // Skip this element
          }

          if (charset) {
            existingElement = document.querySelector(
              `meta[charset="${charset}"]`
            );
          } else if (httpEquiv) {
            existingElement = document.querySelector(
              `meta[http-equiv="${httpEquiv}"]`
            );
          } else if (name) {
            existingElement = document.querySelector(`meta[name="${name}"]`);
          } else if (property) {
            existingElement = document.querySelector(
              `meta[property="${property}"]`
            );
          }
        } else if (tagName === "title") {
          // Skip title tag - let service/offer pages handle it
          return;
        } else if (tagName === "link") {
          const rel = clonedElement.getAttribute("rel");
          const href = clonedElement.getAttribute("href");
          if (rel && href) {
            existingElement = document.querySelector(
              `link[rel="${rel}"][href="${href}"]`
            );
          }
        } else if (tagName === "script") {
          const src = clonedElement.getAttribute("src");
          if (src) {
            existingElement = document.querySelector(`script[src="${src}"]`);
          }
        }

        // Remove existing element if found
        if (existingElement) {
          existingElement.remove();
        }

        // Append to head
        document.head.appendChild(clonedElement);
      });

      // Cleanup function to remove injected elements when component unmounts or tags change
      return () => {
        elements.forEach((element) => {
          const tagName = element.tagName.toLowerCase();
          let elementToRemove = null;

          if (tagName === "meta") {
            const name = element.getAttribute("name");
            const property = element.getAttribute("property");
            const charset = element.getAttribute("charset");
            const httpEquiv = element.getAttribute("http-equiv");

            // Skip keywords and description meta tags in cleanup
            if (name === "keywords" || name === "description") {
              return;
            }

            if (charset) {
              elementToRemove = document.querySelector(
                `meta[charset="${charset}"]`
              );
            } else if (httpEquiv) {
              elementToRemove = document.querySelector(
                `meta[http-equiv="${httpEquiv}"]`
              );
            } else if (name) {
              elementToRemove = document.querySelector(`meta[name="${name}"]`);
            } else if (property) {
              elementToRemove = document.querySelector(
                `meta[property="${property}"]`
              );
            }
          } else if (tagName === "link") {
            const rel = element.getAttribute("rel");
            const href = element.getAttribute("href");
            if (rel && href) {
              elementToRemove = document.querySelector(
                `link[rel="${rel}"][href="${href}"]`
              );
            }
          } else if (tagName === "script") {
            const src = element.getAttribute("src");
            if (src) {
              elementToRemove = document.querySelector(`script[src="${src}"]`);
            }
          }

          if (elementToRemove) {
            elementToRemove.remove();
          }
        });
      };
    }
  }, [headerTags]);

  return null; // This component doesn't render anything
};

export default HeadTags;
